// src/services/ventasService.js
// Centraliza las llamadas al backend del módulo de Ventas Gestionadas.
// Contrato definido en docs/modulo-ventas-gestionadas-PLAN.md (sección 3).

import baseURL from '../Components/url';

const jsonHeaders = { 'Content-Type': 'application/json' };

const handle = async (res) => {
    const data = await res.json().catch(() => ({ error: 'Respuesta inválida del servidor' }));
    if (!res.ok && !data.error) {
        return { error: `Error en la petición: ${res.statusText}` };
    }
    return data;
};

const ventasService = {
    // ---------- Ventas ----------
    /**
     * Lista ventas con filtros, métricas y paginación.
     * @param {object} params { estado, desde, hasta, q, limite, offset }
     */
    listar: async (params = {}) => {
        try {
            const query = new URLSearchParams();
            Object.entries(params).forEach(([k, v]) => {
                if (v !== undefined && v !== null && v !== '') query.append(k, v);
            });
            const res = await fetch(`${baseURL}/ventaGet.php?${query.toString()}`);
            return handle(res);
        } catch (e) {
            console.error('ventasService.listar', e);
            return { error: 'No se pudo cargar el listado de ventas.' };
        }
    },

    /**
     * Crea una venta. Acepta objeto plano (se envía como JSON).
     */
    crear: async (venta) => {
        try {
            const res = await fetch(`${baseURL}/ventaPost.php`, {
                method: 'POST',
                headers: jsonHeaders,
                body: JSON.stringify(venta),
            });
            return handle(res);
        } catch (e) {
            console.error('ventasService.crear', e);
            return { error: 'No se pudo registrar la venta.' };
        }
    },

    /**
     * Edita una venta o cambia su estado.
     */
    actualizar: async (idVenta, cambios) => {
        try {
            const res = await fetch(`${baseURL}/ventaPut.php?idVenta=${idVenta}`, {
                method: 'PUT',
                headers: jsonHeaders,
                body: JSON.stringify(cambios),
            });
            return handle(res);
        } catch (e) {
            console.error('ventasService.actualizar', e);
            return { error: 'No se pudo actualizar la venta.' };
        }
    },

    /**
     * Elimina una venta (y sus abonos por cascade en el backend).
     */
    eliminar: async (idVenta) => {
        try {
            const res = await fetch(`${baseURL}/ventaDelete.php?idVenta=${idVenta}`, {
                method: 'DELETE',
            });
            return handle(res);
        } catch (e) {
            console.error('ventasService.eliminar', e);
            return { error: 'No se pudo eliminar la venta.' };
        }
    },

    // ---------- Abonos ----------
    /**
     * Historial de abonos de una venta.
     */
    listarAbonos: async (idVenta) => {
        try {
            const res = await fetch(`${baseURL}/abonoGet.php?idVenta=${idVenta}`);
            return handle(res);
        } catch (e) {
            console.error('ventasService.listarAbonos', e);
            return { error: 'No se pudieron cargar los abonos.' };
        }
    },

    /**
     * Registra un abono. El backend recalcula saldo y estado.
     * @param {object} abono { idVenta, monto, metodo, nota }
     */
    crearAbono: async (abono) => {
        try {
            const res = await fetch(`${baseURL}/abonoPost.php`, {
                method: 'POST',
                headers: jsonHeaders,
                body: JSON.stringify(abono),
            });
            return handle(res);
        } catch (e) {
            console.error('ventasService.crearAbono', e);
            return { error: 'No se pudo registrar el abono.' };
        }
    },

    /**
     * Revierte un abono. El backend recalcula saldo y estado.
     */
    eliminarAbono: async (idAbono) => {
        try {
            const res = await fetch(`${baseURL}/abonoDelete.php?idAbono=${idAbono}`, {
                method: 'DELETE',
            });
            return handle(res);
        } catch (e) {
            console.error('ventasService.eliminarAbono', e);
            return { error: 'No se pudo eliminar el abono.' };
        }
    },

    // ---------- Gastos (generales del negocio) ----------
    /**
     * Lista gastos con filtros, métricas y paginación.
     * @param {object} params { categoria, desde, hasta, q, limite, offset }
     */
    listarGastos: async (params = {}) => {
        try {
            const query = new URLSearchParams();
            Object.entries(params).forEach(([k, v]) => {
                if (v !== undefined && v !== null && v !== '') query.append(k, v);
            });
            const res = await fetch(`${baseURL}/gastoGet.php?${query.toString()}`);
            return handle(res);
        } catch (e) {
            console.error('ventasService.listarGastos', e);
            return { error: 'No se pudo cargar el listado de gastos.' };
        }
    },

    /**
     * Registra un gasto.
     * @param {object} gasto { concepto, monto, categoria, metodo, nota, fecha }
     */
    crearGasto: async (gasto) => {
        try {
            const res = await fetch(`${baseURL}/gastoPost.php`, {
                method: 'POST',
                headers: jsonHeaders,
                body: JSON.stringify(gasto),
            });
            return handle(res);
        } catch (e) {
            console.error('ventasService.crearGasto', e);
            return { error: 'No se pudo registrar el gasto.' };
        }
    },

    /**
     * Elimina un gasto.
     */
    eliminarGasto: async (idGasto) => {
        try {
            const res = await fetch(`${baseURL}/gastoDelete.php?idGasto=${idGasto}`, {
                method: 'DELETE',
            });
            return handle(res);
        } catch (e) {
            console.error('ventasService.eliminarGasto', e);
            return { error: 'No se pudo eliminar el gasto.' };
        }
    },

    // ---------- Clientes (derivados de las ventas) ----------
    /**
     * Lista/busca clientes únicos con agregados (compras, saldo, última compra).
     * @param {string} q texto de búsqueda (nombre/teléfono/cédula)
     */
    listarClientes: async (q = '') => {
        try {
            const query = new URLSearchParams();
            if (q) query.append('q', q);
            const res = await fetch(`${baseURL}/clientesGet.php?${query.toString()}`);
            const data = await handle(res);
            return data.clientes || [];
        } catch (e) {
            console.error('ventasService.listarClientes', e);
            return [];
        }
    },

    /**
     * Historial de ventas de un cliente + resumen.
     * @param {object} ref { identificacion } o { nombre, telefono }
     */
    historialCliente: async (ref = {}) => {
        try {
            const query = new URLSearchParams({ historial: '1' });
            if (ref.identificacion) {
                query.append('identificacion', ref.identificacion);
            } else {
                if (ref.nombre) query.append('nombre', ref.nombre);
                if (ref.telefono) query.append('telefono', ref.telefono);
            }
            const res = await fetch(`${baseURL}/clientesGet.php?${query.toString()}`);
            return handle(res);
        } catch (e) {
            console.error('ventasService.historialCliente', e);
            return { error: 'No se pudo cargar el historial del cliente.' };
        }
    },

    // ---------- Catálogo (reutiliza endpoint existente) ----------
    /**
     * Lista productos del catálogo para el selector de elemento.
     */
    listarProductos: async () => {
        try {
            const res = await fetch(`${baseURL}/productosGet.php`);
            const data = await handle(res);
            return data.productos || [];
        } catch (e) {
            console.error('ventasService.listarProductos', e);
            return [];
        }
    },
};

export default ventasService;
